<template>
  <div ref="blockRef">
    <div class="grid gap-8 md:grid-cols-2 lg:gap-24 xl:gap-48" data-gsap-target="section">
      <div
        :class="`flex flex-col ${block.image_left ? 'md:order-1' : '' }`"
        data-gsap-target="content"
      >
        <h2
          v-if="block.title"
          v-html="formatTitle(block.title)"
          :class="`text-[clamp(1.5rem,-2.25rem+18.75vw,3.75rem)] leading-none ${block.title_color?.value}`"
          data-gsap-target="title"
        />

        <div
          v-if="block.text"
          v-html="block.text"
          class="prose mt-4 md:mt-8"
          data-gsap-target="text"
        />

        <Buttons
          v-if="block.buttons?.length"
          :buttons="block.buttons"
          class="mt-auto pt-8 xl:pt-12"
          data-gsap-target="buttons"
        />
      </div>

      <div class="ml-auto grow max-w-md" data-gsap-target="image">
        <img
          v-if="block.image"
          :src="block.image.sm.url"
          :srcSet="`
            ${block.image.sm.url} ${block.image.sm.width}w
          `"
          :alt="block.image.sm.alt"
          class="h-72 w-full object-cover rounded-3xl"
          sizes=""
          :width="block.image.sm.width"
          :height="block.image.sm.height"
          loading="lazy"
          draggable="false"
        >
      </div>
    </div>

    <div v-if="block.roadmap?.length" class="mt-32">
      <div
        v-for="(item, index) in block.roadmap"
        :key="item.id"
        :class="`relative grid gap-8 pl-14 md:grid-cols-2 lg:gap-24 xl:gap-48 ${index !== 0 ? 'mt-24' : ''} ${block.image_left ? 'md:pl-0' : '' }`"
        data-gsap-target="section"
      >
        <!-- DOT -->
        <div
          :class="`
            absolute top-1 left-0 z-[1] h-[1.625rem] w-[1.625rem] bg-purple-light rounded-full
            ${block.image_left ? 'md:left-1/2 md:translate-x-4 lg:translate-x-12 xl:translate-x-24' : ''}
          `"
        />

        <!-- INNER DOT -->
        <div
          :class="`
            absolute top-[0.5625rem] z-[2] h-4 w-4 bg-purple-dark rounded-full opacity-0
            ${block.image_left ? 'md:left-1/2 md:translate-x-4 lg:translate-x-[3.3125rem] xl:translate-x-[6.3125rem]' : 'left-[0.3125rem]'}
          `"
          data-gsap-target="dot"
        />

        <!-- LINE -->
        <div
          v-if="!isLast(index, block.roadmap.length)"
          :class="`absolute top-1 left-3 h-[calc(100%+6rem)] w-0.5 bg-purple-light ${block.image_left ? 'md:left-1/2 md:translate-x-7 lg:translate-x-15 xl:translate-x-27' : ''}`"
        />

        <div
          :class="`flex flex-col opacity-50 ${block.image_left ? 'md:order-1 md:pl-14' : '' }`"
          data-gsap-target="content"
        >
          <h3
            v-if="item.roadmap_item_title"
            class="font-serif font-bold text-[2.5rem] text-purple-dark leading-none"
          >
            {{ item.roadmap_item_title }}
          </h3>

          <div
            v-if="item.roadmap_item_text"
            v-html="item.roadmap_item_text"
            class="prose mt-4 md:mt-8"
          />

          <Buttons
            v-if="item.roadmap_item_buttons?.length"
            :buttons="item.roadmap_item_buttons"
            class="mt-auto pt-8 xl:pt-12"
          />
        </div>

        <div class="ml-auto grow max-w-md opacity-0" data-gsap-target="image">
          <img
            v-if="item.roadmap_item_image"
            :src="item.roadmap_item_image.sm.url"
            :srcSet="`
              ${item.roadmap_item_image.sm.url} ${item.roadmap_item_image.sm.width}w
            `"
            :alt="item.roadmap_item_image.sm.alt"
            class="h-72 w-full object-cover rounded-3xl"
            sizes=""
            :width="item.roadmap_item_image.sm.width"
            :height="item.roadmap_item_image.sm.height"
            loading="lazy"
            draggable="false"
            data-gsap-target="image"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Roadmap } from '~/statamic/types/block';

defineProps<{
  block: Roadmap;
}>();

let ctx: gsap.Context | null;
const blockRef = ref();

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const sections = self.selector('[data-gsap-target="section"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top-=50 top',
        end: 'bottom-=50 top',
        scrub: true,
        trigger: blockRef.value,
      },
    });

    if (sections?.length) {
      sections.forEach((section, index) => {
        const image = section.querySelector('[data-gsap-target="image"]');
        const nextSection = sections[index + 1];
        const nextDot = nextSection?.querySelector('[data-gsap-target="dot"]');
        const nextImage = nextSection?.querySelector('[data-gsap-target="image"]');
        const nextContent = nextSection?.querySelector('[data-gsap-target="content"]');

        const blockTop = blockRef.value?.offsetTop || 0;
        const sectionTop = section?.offsetTop || 0;
        const nextSectionTop = nextSection?.offsetTop || 0;

        let toY = (nextSectionTop - blockTop) || 0;

        if (index > 0) {
          toY = (nextSectionTop - sectionTop) || 0;
        }

        if (toY > 0) {
          timeline.to(image, { y: toY });
        }

        timeline.to(image, { opacity: 0 });

        if (nextImage) {
          timeline.to(nextImage, { opacity: 1 }, `label-${index}-=0.5`);
        }

        if (nextDot) {
          timeline.to(nextDot, { opacity: 1 }, `label-${index}-=1`);
        }

        if (nextContent) {
          timeline.to(nextContent, { opacity: 1 }, `label-${index}-=1`);
        }
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
